<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import { officesApi, type Office } from '@/api/offices';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ListToolbar from '@/components/list-toolbar.vue';
import OfficesForm from './offices-form.vue';
import OfficesDiscardForm from './offices-discard-form.vue';
import OfficesUndiscardForm from './offices-undiscard-form.vue';

const { tableProps } = useResponsiveTable();

const selectedOffice = ref<Office | null>(null);
const showForm = ref(false);
const scope = ref<'active' | 'discarded'>('active');

const {
  data: activeOffices,
  refetch: refetchActiveOffices,
  isPending: isLoadingActiveOffices,
} = useQuery({
  queryKey: ['offices', 'active'],
  queryFn: () => officesApi.index('active'),
  initialData: [],
});

const {
  data: discardedOffices,
  refetch: refetchDiscardedOffices,
  isPending: isLoadingDiscardedOffices,
} = useQuery({
  queryKey: ['offices', 'discarded'],
  queryFn: () => officesApi.index('discarded'),
  initialData: [],
});

const offices = computed(() =>
  (scope.value === 'active' ? activeOffices.value : discardedOffices.value),
);

const isLoading = computed(() => isLoadingActiveOffices.value || isLoadingDiscardedOffices.value);

const actionSuccess = ref(false);

function openCreateForm() {
  selectedOffice.value = null;
  showForm.value = true;
}

function openEditForm(office: Office) {
  selectedOffice.value = office;
  showForm.value = true;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveOffices();
  refetchDiscardedOffices();
  actionSuccess.value = true;
}

function handleFormSuccess() {
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedOffice.value = null;
}

const headers = [
  { title: 'Nombre', key: 'name' },
  { title: 'Dirección', key: 'address' },
  { title: 'Comuna', key: 'communeName' },
  { title: 'Creada', key: 'humanCreatedAt' },
  { title: 'Acciones', key: 'actions', sortable: false },
];
</script>
<template>
  <v-card>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="offices"
      :loading="isLoading"
      loading-text="Cargando oficinas..."
    >
      <template #top>
        <list-toolbar title="Oficinas">
          <template #actions>
            <v-btn
              v-if="scope === 'active'"
              class="text-white border bg-primary"
              @click="openCreateForm"
            >
              <v-icon
                start
                icon="mdi-plus"
              />
              Agregar Oficina
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                Activas
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                Archivadas
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
      </template>

      <template #[`item.address`]="{ item }">
        <p class="text-wrap address-cell">
          {{ item.address }}
        </p>
      </template>

      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active'"
          class="d-flex"
        >
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
                variant="text"
                color="warning"
              />
            </template>
            <template #default="{ isActive }">
              <offices-discard-form
                :office="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
        </div>
        <v-dialog
          v-else-if="scope === 'discarded'"
          max-width="600"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
              variant="text"
              color="success"
            />
          </template>
          <template #default="{ isActive }">
            <offices-undiscard-form
              :office="item"
              @close="isActive.value = false"
              @success="handleDiscardAndUndiscardSuccess"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="600"
      scrollable
    >
      <offices-form
        :office="selectedOffice"
        @close="handleFormClose"
        @success="handleFormSuccess"
      />
    </v-dialog>
  </v-card>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
<style scoped>
.address-cell {
  max-width: 300px;
}
</style>
